import { initDataVideoElement } from "./data-video-elements.js";

/**
 * Hero banner background used as the site-loader video (single source, no duplicate `<video>`).
 */
export function getBannerHeroMedia() {
  const section = document.querySelector("section.banner");
  if (!section) return null;

  const media = section.querySelector(".banner__media");
  const mediaElement = section.querySelector(
    ".banner__media .media-element[data-video]"
  );
  if (!media || !mediaElement) return null;

  const video = media.querySelector("video");
  const iframe = media.querySelector("iframe");

  return {
    section,
    media,
    mediaElement,
    bannerImage: section.querySelector(".banner-image"),
    video,
    iframe,
    playbackEl: video ?? iframe ?? null,
  };
}

/**
 * Ensure banner `data-video` is mounted and playing; hide static fallback image during loader.
 * @returns {ReturnType<typeof getBannerHeroMedia> & { playbackEl: HTMLElement } | null}
 */
export function prepareBannerMediaForLoader() {
  const els = getBannerHeroMedia();
  if (!els) return null;

  const mounted = initDataVideoElement(els.mediaElement);
  const video = els.media.querySelector("video");
  const iframe = els.media.querySelector("iframe");
  const playbackEl = mounted ?? video ?? iframe ?? null;
  if (!playbackEl) return null;

  els.section.classList.add("banner--loader-media-active");

  if (els.bannerImage) {
    els.bannerImage.setAttribute("hidden", "");
    els.bannerImage.setAttribute("aria-hidden", "true");
  }

  if (playbackEl instanceof HTMLVideoElement) {
    playbackEl.muted = true;
    playbackEl.play().catch(() => {});
  }

  return { ...els, video, iframe, playbackEl };
}

const VIMEO_ORIGIN = "https://player.vimeo.com";
/** Set by `initDataVideoElement` once the embed has loaded. */
const EMBED_LOADED_CLASS = "data-video--loaded";
/** Gap between "embed loaded" and the first painted frame — the visible start of playback. */
const EMBED_FIRST_FRAME_MS = 900;

function waitForVideoPlaying(video, timeoutMs) {
  return new Promise((resolve) => {
    if (!video.paused && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      resolve(true);
      return;
    }

    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.removeEventListener("playing", onPlaying);
      resolve(ok);
    };
    const onPlaying = () => finish(true);
    const timer = window.setTimeout(() => finish(false), timeoutMs);

    video.addEventListener("playing", onPlaying, { once: true });
    video.muted = true;
    video.play().catch(() => {});
  });
}

function parsePostMessageData(raw) {
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }
  return raw && typeof raw === "object" ? raw : null;
}

/** Player windows that have reported advancing playback, keyed by the iframe's `contentWindow`. */
const playingWindows = new Set();
const playbackListeners = new Set();
let playbackWatchStarted = false;

function subscribeToVimeoEvents(source) {
  if (!source) return;
  ["playing", "playProgress"].forEach((value) => {
    source.postMessage(
      JSON.stringify({ method: "addEventListener", value }),
      VIMEO_ORIGIN
    );
  });
}

function markWindowPlaying(source) {
  if (!source || playingWindows.has(source)) return;
  playingWindows.add(source);
  playbackListeners.forEach((listener) => listener(source));
}

function onEmbedMessage(event) {
  if (event.origin !== VIMEO_ORIGIN) return;

  const data = parsePostMessageData(event.data);
  if (!data) return;

  if (data.event === "ready") {
    subscribeToVimeoEvents(event.source);
    return;
  }
  // `playing` and advancing `playProgress` mean frames are on screen; `play` fires while buffering.
  if (data.event === "playing") {
    markWindowPlaying(event.source);
    return;
  }
  if (data.event === "playProgress" && Number(data.data?.seconds) > 0) {
    markWindowPlaying(event.source);
  }
}

/**
 * Vimeo only delivers player events to subscriptions made in response to its one-shot `ready`
 * message, so this has to be listening before the embed loads — call it at script parse time,
 * not from the `load` handler.
 */
export function startBannerMediaPlaybackWatch() {
  if (playbackWatchStarted || typeof window === "undefined") return;
  playbackWatchStarted = true;
  window.addEventListener("message", onEmbedMessage);
}

function waitForVimeoPlaying(iframe, timeoutMs) {
  return new Promise((resolve) => {
    startBannerMediaPlaybackWatch();

    if (playingWindows.has(iframe.contentWindow)) {
      resolve(true);
      return;
    }

    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      playbackListeners.delete(onPlayback);
      iframe.removeEventListener("load", onLoad);
      resolve(ok);
    };

    const onPlayback = (source) => {
      if (source === iframe.contentWindow) finish(true);
    };
    const onLoad = () => subscribeToVimeoEvents(iframe.contentWindow);
    const timer = window.setTimeout(() => finish(false), timeoutMs);

    playbackListeners.add(onPlayback);
    iframe.addEventListener("load", onLoad);
    // Covers a player that was already ready before the watch started.
    subscribeToVimeoEvents(iframe.contentWindow);
  });
}

/**
 * YouTube (without its JS API) reports nothing cross-origin, so playback start is approximated
 * from "embed loaded" plus the first-frame gap.
 */
function waitForEmbedLoaded(iframe, container, timeoutMs) {
  return new Promise((resolve) => {
    let settled = false;
    let firstFrameTimer = 0;

    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.clearTimeout(firstFrameTimer);
      iframe.removeEventListener("load", onLoad);
      resolve(ok);
    };

    const finishAfterFirstFrame = () => {
      if (settled || firstFrameTimer) return;
      firstFrameTimer = window.setTimeout(() => finish(true), EMBED_FIRST_FRAME_MS);
    };

    const onLoad = () => finishAfterFirstFrame();
    const timer = window.setTimeout(() => finish(false), timeoutMs);

    iframe.addEventListener("load", onLoad, { once: true });

    if (container?.classList.contains(EMBED_LOADED_CLASS)) {
      finishAfterFirstFrame();
    }
  });
}

/**
 * Resolves when banner media playback has started; `false` when it times out.
 * @param {{ playbackEl?: HTMLElement | null, media?: Element | null, mediaElement?: Element | null } | null} bannerMedia
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<boolean>}
 */
export function waitForBannerMediaPlaying(bannerMedia, options = {}) {
  const { timeoutMs = 20000 } = options;
  const el =
    bannerMedia?.playbackEl ??
    bannerMedia?.media?.querySelector("video, iframe") ??
    null;

  if (!el) return Promise.resolve(false);
  if (el instanceof HTMLVideoElement) return waitForVideoPlaying(el, timeoutMs);
  if (!(el instanceof HTMLIFrameElement)) return Promise.resolve(false);
  if (el.src.startsWith(VIMEO_ORIGIN)) return waitForVimeoPlaying(el, timeoutMs);

  return waitForEmbedLoaded(el, bannerMedia?.mediaElement ?? null, timeoutMs);
}

/** Restore banner image visibility after the loader finishes. */
export function teardownBannerMediaAfterLoader(bannerMedia) {
  if (!bannerMedia?.section) return;

  bannerMedia.section.classList.remove("banner--loader-media-active");

  if (bannerMedia.bannerImage) {
    bannerMedia.bannerImage.removeAttribute("hidden");
    bannerMedia.bannerImage.removeAttribute("aria-hidden");
  }
}
